import React from 'react'
import { CCard, CCardBody, CPlaceholder } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'

/** 12480 -> "12,480". Nulls render as an em dash rather than "0" or "NaN". */
export const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return Number(value).toLocaleString()
}

export const formatDecimal = (value, places = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return Number(value).toFixed(places)
}

/**
 * Every number on this dashboard carries a comparison — a bare count tells an
 * admin nothing. Returns the direction, the percentage and a colour.
 *
 * Growth from zero has no meaningful percentage (it is not "infinity% up"), so
 * it is reported as "new" rather than a fabricated number.
 */
export const computeDelta = (current, previous) => {
  const cur = Number(current) || 0
  const prev = Number(previous) || 0

  if (prev === 0 && cur === 0) {
    return { direction: 'flat', label: 'No change', color: 'medium-emphasis', pct: 0 }
  }
  // No baseline to compare against. "New" would read wrongly on an average
  // (screens-per-session is not new, it simply had no prior period), and a
  // percentage from zero is not a real number.
  if (prev === 0) {
    return { direction: 'up', label: 'No prior data', color: 'medium-emphasis', pct: null }
  }

  const pct = ((cur - prev) / prev) * 100
  const rounded = Math.abs(pct) < 0.05 ? 0 : pct

  if (rounded === 0) {
    return { direction: 'flat', label: 'No change', color: 'medium-emphasis', pct: 0 }
  }

  return {
    direction: rounded > 0 ? 'up' : 'down',
    label: `${rounded > 0 ? '▲' : '▼'} ${Math.abs(rounded).toFixed(1)}%`,
    color: rounded > 0 ? 'success' : 'danger',
    pct: rounded,
  }
}

/** "26 Aug" / "26 Aug 2026" — avoids ambiguous 08/09 ordering for AU vs US admins. */
export const formatDateLabel = (isoDate, withYear = false) => {
  if (!isoDate) return ''
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  })
}

export const formatRangeLabel = (from, to) => {
  if (!from || !to) return ''
  return `${formatDateLabel(from)} – ${formatDateLabel(to, true)}`
}

/**
 * Per-tile skeletons rather than one full-page spinner, so the layout does not
 * jump and the admin can see what is arriving.
 */
/**
 * House-pattern stat card: label + big value on the left, a tinted icon puck on
 * the right (README "Listing pages and tables" §2). `h-100` on every card plus
 * a fixed-height sparkline slot keeps all six the same height even when only
 * some metrics have a per-bucket series — the uneven heights were the
 * "cards are not standard sizes" problem.
 *
 * The puck tint is written explicitly in AppAnalytics.css as
 * rgba(var(--cui-*-rgb), .12): `bg-*-subtle` does not exist in CoreUI Pro 4.6
 * and renders untinted, per the warning in the README.
 */
export const StatCard = ({ label, value, delta, icon, color = 'primary', children }) => (
  <CCard className="h-100 av2-stat-card">
    <CCardBody className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="min-w-0">
          <div className="text-medium-emphasis small text-truncate" title={label}>
            {label}
          </div>
          <div className="fs-4 fw-semibold lh-sm">{value}</div>
          {delta && (
            <div className={`small mt-1 text-${delta.color}`} title="vs previous period">
              {delta.label}
            </div>
          )}
        </div>
        {icon && (
          <div
            className={`av2-stat-puck av2-stat-puck--${color} d-flex align-items-center justify-content-center rounded-circle flex-shrink-0`}
          >
            <CIcon icon={icon} size="lg" />
          </div>
        )}
      </div>
      {/* Fixed-height slot: present on every card whether or not it has a
          sparkline, so the cards line up. */}
      <div className="av2-stat-spark mt-auto">{children}</div>
    </CCardBody>
  </CCard>
)

export const TileSkeleton = ({ height = 110 }) => (
  <CCard className="mb-4 av2-skeleton" style={{ minHeight: height }}>
    <CCardBody>
      <CPlaceholder component="div" animation="glow">
        <CPlaceholder xs={5} className="mb-2" />
        <CPlaceholder xs={8} size="lg" className="mb-2" />
        <CPlaceholder xs={4} />
      </CPlaceholder>
    </CCardBody>
  </CCard>
)

export const ChartSkeleton = ({ height = 300 }) => (
  <div className="av2-skeleton d-flex align-items-end gap-2 px-2" style={{ height }}>
    <CPlaceholder component="div" animation="glow" className="w-100 d-flex align-items-end gap-2">
      {[55, 80, 40, 95, 65, 75, 50].map((h, i) => (
        <CPlaceholder key={i} style={{ height: `${h}%`, flex: 1, borderRadius: '4px 4px 0 0' }} />
      ))}
    </CPlaceholder>
  </div>
)

/**
 * A real empty state. Never an empty axis, never a spinner that never stops.
 */
export const EmptyState = ({
  title = 'No app activity in this period',
  hint = 'Try widening the date range, or clearing the platform and version filters.',
  height = 300,
}) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center text-center px-3"
    style={{ minHeight: height }}
  >
    <div className="fs-5 fw-semibold mb-1">{title}</div>
    <div className="text-medium-emphasis small" style={{ maxWidth: 420 }}>
      {hint}
    </div>
  </div>
)

/** Surfaced when a request fails, so a failure never looks like "zero traffic". */
export const ErrorState = ({ message, onRetry, height = 300 }) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center text-center px-3"
    style={{ minHeight: height }}
  >
    <div className="fs-6 fw-semibold text-danger mb-1">Could not load this section</div>
    <div className="text-medium-emphasis small mb-3" style={{ maxWidth: 420 }}>
      {message || 'The analytics service did not respond.'}
    </div>
    {onRetry && (
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
        Try again
      </button>
    )}
  </div>
)

/**
 * Charts stay readable at ~10 categories. Anything past the cap is summed into
 * a single "Other" row rather than dropped, so the total still adds up.
 */
export const capWithOther = (rows, limit, valueKeys, labelKey) => {
  if (!Array.isArray(rows) || rows.length <= limit) return rows || []

  const head = rows.slice(0, limit)
  const tail = rows.slice(limit)

  const other = { [labelKey]: `Other (${tail.length})`, __isOther: true }
  valueKeys.forEach((key) => {
    other[key] = tail.reduce((sum, row) => sum + (Number(row[key]) || 0), 0)
  })

  return [...head, other]
}
